/**
 * FeatureCards3D — the six flagship feature cards with a REAL pointer-tracking
 * 3D tilt (CSS perspective + rotateX/rotateY driven by the cursor position,
 * with a layered lift shadow and a subtle inner sheen). The whole effect is
 * disabled under `prefers-reduced-motion` (the card still lifts flat on hover).
 */

import { type MouseEvent, useRef } from 'react';

import { FEATURE_CARDS, type FeatureCard } from '@/routes/landing/landing-data';
import { IconChip, SectionHeading } from './shared';

/** True when the visitor asked for reduced motion (checked at interaction time). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function FeatureCards3D() {
  return (
    <section id="fonctionnalites" className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
      <SectionHeading
        eyebrow="Fonctionnalités"
        title="Une plateforme, six super-pouvoirs"
        subtitle="Tout ce dont vous avez besoin pour créer, publier, prospecter et mesurer — réuni au même endroit."
      />

      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURE_CARDS.map((card) => (
          <TiltCard key={card.title} card={card} />
        ))}
      </div>
    </section>
  );
}

function TiltCard({ card }: { card: FeatureCard }) {
  const ref = useRef<HTMLDivElement>(null);

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0 → 1
    const py = (e.clientY - rect.top) / rect.height; // 0 → 1
    const rotateY = (px - 0.5) * 12; // left/right tilt (deg)
    const rotateX = (0.5 - py) * 12; // up/down tilt (deg)
    el.style.setProperty('--rx', `${rotateX.toFixed(2)}deg`);
    el.style.setProperty('--ry', `${rotateY.toFixed(2)}deg`);
    // Move the sheen highlight toward the cursor.
    el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
  };

  const reset = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  return (
    <div
      className="group [perspective:1200px]"
      onMouseMove={handleMove}
      onMouseLeave={reset}
    >
      <div
        ref={ref}
        style={{ transform: 'rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg))' }}
        className="relative isolate flex h-full flex-col overflow-hidden rounded-token-lg border border-border bg-surface p-6 shadow-elevation-1 transition-[transform,box-shadow] duration-200 ease-out [transform-style:preserve-3d] will-change-transform group-hover:-translate-y-1 group-hover:border-primary/40 group-hover:shadow-elevation-3 motion-reduce:!transform-none motion-reduce:transition-none"
      >
        {/* Cursor-following sheen (motion-safe, decorative). */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100 motion-reduce:hidden"
          style={{
            background:
              'radial-gradient(320px circle at var(--mx,50%) var(--my,0%), rgb(var(--color-primary) / 0.14), transparent 60%)',
          }}
        />

        <div style={{ transform: 'translateZ(28px)' }}>
          <IconChip name={card.icon} tone={card.tone} />
        </div>
        <h3 style={{ transform: 'translateZ(18px)' }} className="mt-5 text-xl font-bold text-foreground">
          {card.title}
        </h3>
        <p style={{ transform: 'translateZ(10px)' }} className="mt-2.5 text-pretty leading-relaxed text-muted">
          {card.description}
        </p>
        <span
          style={{ transform: 'translateZ(10px)' }}
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary opacity-0 transition-opacity duration-200 group-hover:opacity-100 motion-reduce:opacity-100"
        >
          En savoir plus
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-4 w-4">
            <path d="M4.5 12h15m0 0-6-6m6 6-6 6" />
          </svg>
        </span>
      </div>
    </div>
  );
}
